import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  OnDestroy,
  TemplateRef,
  computed,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbbSwitchComponent } from '@components/abb-switch/abb-switch.component';
import {
  AbbTableComponent,
  DataTableFilter,
  FilterOption,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { Apollo } from 'apollo-angular';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  AutomationsDefinitionsQuery,
  AutomationsDefinitionsQueryVariables,
  Status,
  OutcomeIndicator,
  ToggleAutomationEnabledCtrlMutation,
  ToggleAutomationEnabledCtrlMutationVariables,
  DeleteAutomationsMutation,
  DeleteAutomationsMutationVariables,
  PropertySelectOptionsQuery,
  PropertySelectOptionsQueryVariables,
  GroupSelectOptionsQuery,
  GroupSelectOptionsQueryVariables,
  DeviceSelectOptionsQuery,
  DeviceSelectOptionsQueryVariables,
} from 'gql/graphql';
import { BehaviorSubject, first, Subscription } from 'rxjs';
import { dataProviderFromObservable } from '@utils/data-provider';
import {
  automationsDefinitionsQuery,
  deleteAutomationsMutation,
  toggleAutomationEnabledCtrlMutation,
} from '@graphql/automations';
import { timeSince } from '@utils/datetime';
import {
  capitalizeFirstLetter,
  replaceTemplatePlaceholders,
  t,
} from '@utils/strings';
import { RouterModule } from '@angular/router';
import { CONTROLS_SUBTITLE } from '@pages/controls/controls';
import { TranslateService } from '@ngx-translate/core';
import { SELECTION } from '@utils/table';
import { OverlayService } from '@services/ui/overlay.service';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { ToastService } from '@services/ui/toast.service';
import { AutomationsSidePanelComponent } from '../../automations-side-panel/automations-side-panel.component';
import { jsonParsePipe } from '@pipes/jsonParse.pipe';
import { TimeAgoPipe } from '@pipes/timeAgo.pipe';
import { propertySelectOptionsQuery } from '@graphql/property';
import { groupSelectOptionsQuery } from '@graphql/group';
import { deviceSelectOptionsQuery } from '@graphql/asset-management';
import { LOADING } from '@utils/loader';
import { getErrorMessage } from '@utils/errors';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { PermissionsService } from '@services/user/permissions.service';
import { PAGES } from '@constants/constants';

type AutomationsDefinitionsDataTableFilter = {
  automationName: string | null;
  alarmEnabled: boolean | null;
  automationEnabled: boolean | null;
  groupId: string | null;
  deviceId: string | null;
  propertyId: string | null;
};

@Component({
  selector: 'abb-definitions',
  standalone: true,
  imports: [
    AbbTableComponent,
    CommonModule,
    TPipe,
    AbbSwitchComponent,
    FormsModule,
    AbbDateComponent,
    RouterModule,
    jsonParsePipe,
    TimeAgoPipe,
    OverflowTooltipDirective,
  ],
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [filters]="filters()"
      [defaultFilters]="defaultFilters"
      [loading]="loading()"
      [selectable]="canEdit()"
      [actions]="actions"
    ></abb-table>
    <ng-template #when let-item>
      <div class="flex gap-2 items-center">
        <span overflowTooltip class="truncate">
          <!-- <span>{{ item.when.name }}:</span> -->
          @if(item.when.interval.fromDate){
          <abb-date
            [date]="item.when.interval.fromDate"
            [showTime]="true"
            [showTooltip]="false"
          ></abb-date
          >} - @if(item.when.interval.toDate){<abb-date
            [date]="item.when.interval.toDate"
            [showTime]="true"
            [showTooltip]="false"
          ></abb-date>
          }
        </span>
      </div>
    </ng-template>
    <ng-template #if let-item>
      <span overflowTooltip class="block w-full truncate">
        {{
          (item.if.conditions?.[0].deviceCategory?.name | t) ||
            ((item.groups?.length > 0 ? 'Group' : 'Property') | t)
        }}
        {{ (item.if.conditions?.[0].eventType.name | t).toLowerCase() }}
        {{ item.if.conditions?.[0]?.threshold+(item.if.conditions?.[0]?.measure?.name | t) }}
      </span>
    </ng-template>
    <ng-template #then let-item>
      @if(item.then.action.nameWithPlaceholder) {
      <span
        overflowTooltip
        class="block truncate"
        *ngIf="replaceTemplatePlaceholders(item.then.action.nameWithPlaceholder, {
            device: '&lt;a href=&quot;/asset-management/devices/'+item.then.device.id+'&quot; class=&quot;text-abb-primary/80 font-medium hover:text-abb-primary transition-colors cursor-pointer&quot;&gt;'+item.then.device.name+'&lt;/a&gt;',
          }, true) | jsonParse as parsedHtml"
        [innerHTML]="parsedHtml.value"
      >
      </span>
      } @else {
      <span class="block truncate" overflowTooltip>
        {{ item.then.action.name }}
      </span>
      }
    </ng-template>
    <ng-template #lastUpdate let-item>
      @if (item.lastUpdate === null) {
      {{ '-' }}
      } @else if(item.status === OutcomeIndicator.Ko) {
      <span class="block truncate" overflowTooltip>
        {{ 'Failed' | t }}
      </span>
      } @else {
      <span class="block truncate" overflowTooltip>
        {{ (item.lastUpdate | timeAgo | async)?.value ?? '' }}
        {{ (item.lastUpdate | timeAgo | async)?.label | t }}
      </span>
      }
    </ng-template>
    <!-- <ng-template #alarm let-item>
      {{
        (item.alarm.status === Status.Ok || item.alarm.status === Status.Warning
          ? 'No'
          : 'Yes'
        ) | t
      }}
    </ng-template> -->
    <ng-template #enabled let-item>
      <div class="flex justify-center items-center">
        <abb-switch
          id="toggle-automation"
          [(ngModel)]="automationsEnabled()[item.id]"
          (click)="toggleAutomation(item.id, $event)"
        ></abb-switch>
      </div>
    </ng-template>
    <ng-template #properties let-item>
      <span overflowTooltip class="block truncate">
        @for (p of item.properties; track p.name) {
        {{ p.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #devices let-item>
      <span overflowTooltip class="block truncate">
        @for (d of item.devices; track d.name) {
        {{ d.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #groups let-item>
      <span overflowTooltip class="block truncate">
        @for (g of item.groups; track g.name) {
        {{ g.name }}@if (!$last) {, } }
      </span>
    </ng-template>
  `,
  styles: ``,
  host: {
    class: 'mt-2',
  },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'controls.tabs.automations.tabs.configured',
    },
  ],
})
export class DefinitionsComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'definitions-component';
  readonly Status = Status;
  readonly OutcomeIndicator = OutcomeIndicator;
  readonly replaceTemplatePlaceholders = replaceTemplatePlaceholders;
  readonly timeSince = timeSince;
  subtitle = inject(CONTROLS_SUBTITLE);
  lang = signal('');
  canEdit = computed(() =>
    this.permissionsService.canEditPage(PAGES.ASSETMANAGEMENT)
  );

  whenTemplate = viewChild<TemplateRef<unknown>>('when');
  ifTemplate = viewChild<TemplateRef<unknown>>('if');
  thenTemplate = viewChild<TemplateRef<unknown>>('then');
  htmlThen = model([]);
  enabledTemplate = viewChild<TemplateRef<unknown>>('enabled');
  lastUpdateTemplate = viewChild<TemplateRef<unknown>>('lastUpdate');
  // alarmTemplate = viewChild<TemplateRef<unknown>>('alarm');
  propertiesTemplate = viewChild<TemplateRef<unknown>>('properties');
  groupsTemplate = viewChild<TemplateRef<unknown>>('groups');
  devicesTemplate = viewChild<TemplateRef<unknown>>('devices');

  definitions$ = new BehaviorSubject<
    AutomationsDefinitionsQuery['automationsDefinitions'] | []
  >([]);
  automationsEnabled = model<Record<string, boolean | undefined>>({});
  deviceSelectOptions = signal<FilterOption[]>([]);
  propertySelectOptions = signal<FilterOption[]>([]);
  groupSelectOptions = signal<FilterOption[]>([]);

  dataProvider = dataProviderFromObservable<
    AutomationsDefinitionsQuery['automationsDefinitions'][number],
    AutomationsDefinitionsDataTableFilter
  >(this.definitions$, (el, filter) => {
    return (
      (!filter.automationEnabled || el.enabled === filter.automationEnabled) &&
      (!filter.automationName ||
        el.name.toLowerCase().includes(filter.automationName.toLowerCase())) &&
      (!filter.groupId ||
        (el.groups?.some((g) => g?.id === filter.groupId) ?? false)) &&
      (!filter.propertyId ||
        (el.properties?.some((p) => p && p.id === filter.propertyId) ??
          false)) &&
      (!filter.deviceId ||
        (el.devices?.some((d) => d && d.id === filter.deviceId) ?? false))
    );
  });

  loading = signal(true);
  prefix = inject(TRANSLATION_PREFIX);
  columns = computed<
    IColumn<AutomationsDefinitionsQuery['automationsDefinitions'][number]>[]
  >(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        key: 'name',
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'when',
        name: 'When',
        templateRef: this.whenTemplate(),
        translate: true,
      },
      {
        id: 'if',
        name: 'If',
        templateRef: this.ifTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'then',
        name: 'Then',
        templateRef: this.thenTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'lastUpdate',
        name: 'Last update',
        translate: true,
        templateRef: this.lastUpdateTemplate(),
      },
      // {
      //   id: 'alarm',
      //   name: 'Alarm',
      //   templateRef: this.alarmTemplate(),
      //   translate: true,
      //   tableCellClass: 'font-medium',
      // },
      {
        id: 'enabled',
        name: 'Enabled',
        templateRef: this.enabledTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
        tableHeaderCellClass: '[&>*]:!justify-center',
      },
      {
        id: 'devices',
        name: 'Devices',
        templateRef: this.devicesTemplate(),
        translate: true,
      },
    ];
  });
  filters = computed<DataTableFilter<AutomationsDefinitionsDataTableFilter>[]>(
    () => {
      return [
        {
          id: 'automationName',
          filterType: 'text',
          className: 'w-full',
          label: 'Automation Name',
        },
        {
          id: 'deviceId',
          filterOptions: this.deviceSelectOptions(),
          filterType: 'select',
          className: 'w-full',
          label: 'Device',
        },
        // {
        //   id: 'propertyId',
        //   filterOptions: this.propertySelectOptions(),
        //   filterType: 'select',
        //   className: 'w-full',
        //   label: 'Property',
        // },
        // {
        //   id: 'groupId',
        //   filterOptions: this.groupSelectOptions(),
        //   filterType: 'select',
        //   className: 'w-full',
        //   label: 'Group',
        // },
        // {
        //   id: 'alarmEnabled',
        //   filterType: 'switch',
        //   className: 'w-full inline-flex justify-start items-center',
        //   label: 'Show with alarm only',
        // },
        {
          id: 'automationEnabled',
          filterType: 'switch',
          className: 'w-full',
          label: 'Show enabled automations only',
        },
      ];
    }
  );
  defaultFilters: AutomationsDefinitionsDataTableFilter = {
    automationName: null,
    alarmEnabled: null,
    automationEnabled: null,
    groupId: null,
    deviceId: null,
    propertyId: null,
  };
  actions: Array<
    MenuItem<AutomationsDefinitionsQuery['automationsDefinitions'][number]>
  > = [
    {
      icon: 'mat-outlined/check_circle',
      label: 'Enable',
      action: (items) => this.toggleAllAutomations(items, { enable: true }),
      selectionAllowed: SELECTION.MULTIPLE,
    },
    {
      icon: 'mat-outlined/block',
      label: 'Disable',
      action: (items) => this.toggleAllAutomations(items, { enable: false }),
      selectionAllowed: SELECTION.MULTIPLE,
    },
    // {
    //   icon: 'mat-outlined/notifications_active',
    //   label: 'Alarm',
    //   action: (items) => this.setAlarmsForAllAutomations(items),
    //   selectionAllowed: SELECTION.MULTIPLE,
    // },
    {
      icon: 'mat-outlined/edit',
      label: 'Edit',
      action: (items) => this.openEditPanel(items[0]),
      selectionAllowed: SELECTION.SINGLE,
    },
    {
      icon: 'mat-outlined/delete',
      label: 'Delete',
      action: (items) => this.openDeleteModal(items),
      selectionAllowed: SELECTION.ALWAYS,
    },
    {
      icon: 'mat-outlined/content_copy',
      label: 'Clone',
      action: (items) => this.openEditPanel(items[0], { mode: 'clone' }),
      selectionAllowed: SELECTION.SINGLE,
    },
  ];

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly overlayService: OverlayService,
    private readonly toast: ToastService,
    private readonly permissionsService: PermissionsService
  ) {
    this.lang.set(translate.currentLang);
    this.subscriptions.push(
      apollo
        .watchQuery<AutomationsDefinitionsQuery, AutomationsDefinitionsQueryVariables>({
          query: automationsDefinitionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            // Ricalcolo fuso orario locale
            const adjustDate = (dateInput?: string | Date | null): Date => {
              const base = dateInput
                ? (() => {
                    const parsedDate = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
                    return parsedDate;
                  })()
                : new Date(); // fallback a now o a new Date(0)
            
              const offsetMs = base.getTimezoneOffset() * 60 * 1000;
              return new Date(base.getTime() + offsetMs);
            };
            
            
    
            // Clono i dati originali e modifico fromDate/toDate
            const adjustedDefinitions = data.automationsDefinitions.map((def) => {
              const interval = def.when?.interval;
    
              return {
                ...def,
                when: interval
                  ? {
                      ...def.when,
                      interval: {
                        ...interval,
                        fromDate: adjustDate(interval.fromDate),
                        toDate: adjustDate(interval.toDate),
                      },
                    }
                  : def.when,
              };
            });
            const runAutomations = adjustedDefinitions.filter(
              (d) => d.status === OutcomeIndicator.Ok && d.lastUpdate != null
            );
            const now = new Date();
            const fiveDaysAgo = new Date(now);
            fiveDaysAgo.setDate(now.getDate() - 5);
            const lastRunAutomations = runAutomations.filter(
              (a) => a.lastUpdate! >= fiveDaysAgo
            );
    
            this.definitions$.next(adjustedDefinitions);
    
            this.automationsEnabled.set(
              adjustedDefinitions.reduce((acc, curr) => {
                return {
                  ...acc,
                  [curr.id]: curr.enabled,
                };
              }, {})
            );
    
            const key =
              adjustedDefinitions.length > 0
                ? 'You have a total of {tot} automations configured. In the last 5 days, {lastRan} have run and {failed} were not possible to be executed'
                : 'You have no automations configured yet';
    
            this.subtitle.set({
              key,
              params: {
                tot: adjustedDefinitions.length,
                lastRan: lastRunAutomations.length,
                failed: adjustedDefinitions.filter(
                  (a) =>
                    a.status === OutcomeIndicator.Ko && a.lastUpdate != null
                ).length,
              },
            });
    
            this.loading.set(loading);
          },
          error: (err) => {
            this.loading.set(false);
            // const errMsg = getErrorMessage(err, this.translate);
            // this.toast.notifyError(errMsg);
          },
        })
    ); 
  }

  // setAlarmsForAllAutomations(
  //   items: AutomationsDefinitionsQuery['automationsDefinitions']
  // ) {
  //   return new Promise<void>((resolve, reject) => {
  //     const automationsToSetAlarmFor = items.filter((i) => {
  //       return i.alarm.status !== Status.Alarm;
  //     });
  //     if (automationsToSetAlarmFor && automationsToSetAlarmFor.length === 0) {
  //       this.toast.notifyWarning(
  //         t(
  //           this.prefix,
  //           `Selected automations are already in alarm status`,
  //           this.translate
  //         )
  //       );
  //     } else {
  //       this.openConfirmModal(
  //         'set in alarm',
  //         items.length > 1 ? undefined : items[0]
  //       )
  //         .then(() => {
  //           this.awaitingRes.set(true);
  //           this.setAlarmAutomations(items.map((a) => a.id))
  //             .then(resolve)
  //             .finally(() => this.awaitingRes.set(false));
  //         })
  //         .catch((err) => {
  //           console.log('cancel');
  //           //reject(err);
  //         });
  //     }
  //   });
  // }

  toggleAllAutomations(
    items: AutomationsDefinitionsQuery['automationsDefinitions'],
    opts: { enable: boolean }
  ) {
    return new Promise<void>((resolve, reject) => {
      const automationsToToggle = items.filter((i) => {
        if (opts.enable) {
          return !i.enabled;
        }
        return i.enabled;
      });
      const action = opts.enable ? 'enable' : 'disable';
      if (automationsToToggle && automationsToToggle.length === 0) {
        this.toast.notifyWarning(
          t(
            this.prefix,
            `Selected automations are already {status}`,
            this.translate,
            {
              status: t(
                this.prefix,
                opts.enable ? 'enabled_other' : 'disabled_other',
                this.translate
              ),
            }
          )
        );
      } else {
        this.openConfirmModal(action)
          .then(() => {
            if (automationsToToggle) {
              this.awaitingRes.set(true);
              this.updateAutomationsEnabled(
                automationsToToggle.map((a) => ({
                  id: a.id,
                  enabled: opts.enable,
                }))
              )
                .then(resolve)
                .finally(() => this.awaitingRes.set(false));
            }
          })
          .catch((err) => {
            console.log('cancel');
            //reject(err);
          });
      }
    });
  }

  toggleAutomation(id: string, event: Event) {
    event.preventDefault();
    const row = this.definitions$.value?.find((c) => c.id === id);
    const newValue = !row?.enabled;
    if (newValue !== undefined && newValue !== row?.enabled) {
      const action = newValue ? 'enable' : 'disable';
      this.openConfirmModal(action, row)
        .then(() => {
          this.awaitingRes.set(true);
          this.updateAutomationsEnabled([
            {
              id,
              enabled: newValue,
            },
          ]).finally(() => this.awaitingRes.set(false));
        })
        .catch(() => console.log('cancel'));
    }
  }

  openConfirmModal(
    action: string,
    row?:
      | AutomationsDefinitionsQuery['automationsDefinitions'][number]
      | undefined
  ) {
    return this.overlayService.openAlert({
      message: `${t(
        this.prefix,
        'Are you sure that you want to {action}',
        this.translate,
        { action: t(this.prefix, action, this.translate) }
      )} ${
        row
          ? `<b>${row?.name}</b>`
          : t(this.prefix, 'all selected items', this.translate)
      }?`,
      htmlMessage: true,
      confirmText: t(
        this.prefix,
        capitalizeFirstLetter(action),
        this.translate
      ),
    });
  }

  updateAutomationsEnabled(input: { id: string; enabled: boolean }[]) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<
          ToggleAutomationEnabledCtrlMutation,
          ToggleAutomationEnabledCtrlMutationVariables
        >({
          mutation: toggleAutomationEnabledCtrlMutation,
          variables: {
            input,
          },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.apollo.client.refetchQueries({
              include: ['AutomationsDefinitions']
            })
            this.toast.notifySuccess(
              t(
                this.prefix,
                `Automation {action} successfully`,
                this.translate,
                {
                  action: input.some((i) => i.enabled)
                    ? t(this.prefix, 'enabled', this.translate)
                    : t(this.prefix, 'disabled', this.translate),
                }
              )
            );
          },
          error: () => {
            reject();
            this.toast.notifySuccess(
              t(
                this.prefix,
                `An error occurred while {action} automation`,
                this.translate,
                {
                  action: input.some((i) => i.enabled)
                    ? t(this.prefix, 'enabling', this.translate)
                    : t(this.prefix, 'disabling', this.translate),
                }
              )
            );
          },
        })
    );
  }

  openEditPanel(
    item: AutomationsDefinitionsQuery['automationsDefinitions'][number],
    opts: { mode: 'edit' | 'clone' } = { mode: 'edit' }
  ) {
    const sidePanel = this.overlayService.openSidePanel(
      AutomationsSidePanelComponent,
      {
        mode: opts.mode,
        defaultData: item,
      }
    );
    return new Promise<void>((resolve, reject) => {
      sidePanel.componentInstance.onSubmitted.subscribe((success) => {
        if (success) {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  awaitingRes = inject(LOADING);
  openDeleteModal(
    items: AutomationsDefinitionsQuery['automationsDefinitions']
  ) {
    return new Promise<void>((resolve, reject) => {
      this.openConfirmModal('remove', items.length > 1 ? undefined : items[0])
        .then(() => {
          this.awaitingRes.set(true);
          this.deleteAutomations(items.map((a) => a.id))
            .then(resolve)
            .finally(() => {
              this.awaitingRes.set(false)
              this.apollo.client.refetchQueries({
                include: ['AutomationsDefinitions']
              })
            });
        })
        .catch((err) => {
          console.log('cancel');
          reject(err);
        });
    });
  }

  deleteAutomations(ids: string | string[]) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<DeleteAutomationsMutation, DeleteAutomationsMutationVariables>({
          mutation: deleteAutomationsMutation,
          variables: {
            ids,
          },
          update: (cache, { data }) => {
            cache.writeQuery({
              query: automationsDefinitionsQuery,
              data: { automationsDefinitions: data?.deleteAutomations },
            });
          },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, `Automation deleted successfully`, this.translate)
            );
          },
          error: () => {
            reject();
            this.toast.notifySuccess(
              t(
                this.prefix,
                `An error occurred while deleting the automation`,
                this.translate
              )
            );
          },
        })
    );
  }

  // setAlarmAutomations(ids: string | string[]) {
  //   return new Promise<void>((resolve, reject) =>
  //     this.apollo
  //       .mutate<
  //         SetAlarmAutomationMutation,
  //         SetAlarmAutomationMutationVariables
  //       >({
  //         mutation: setAlarmAutomationMutation,
  //         variables: {
  //           ids,
  //         },
  //       })
  //       .pipe(first())
  //       .subscribe({
  //         next: () => {
  //           resolve();
  //           this.apollo.client.refetchQueries({
  //             include: ['AutomationsDefinitions']
  //           })
  //           this.toast.notifySuccess(
  //             t(
  //               this.prefix,
  //               `Automation set successfully in alarm`,
  //               this.translate
  //             )
  //           );
  //         },
  //         error: () => {
  //           reject();
  //           this.toast.notifySuccess(
  //             t(
  //               this.prefix,
  //               `An error occurred while setting automation in alarm`,
  //               this.translate
  //             )
  //           );
  //         },
  //       })
  //   );
  // }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
